import { Observable, throwError } from 'rxjs';
import { Injectable } from '@angular/core';

export interface SgEntitySelectEntityFetchMap {
  _CUSTOM_: {
    entity: any;
    query: any;
  };
}

export type InfiniteScrollInfo = {
  currentPage: number;
  totalPages: number;
};

export type SgEntitySelectSearchResponse<T extends keyof SgEntitySelectEntityFetchMap> = {
  data: Array<TEntity<T>>;
  infiniteScrollInfo?: InfiniteScrollInfo;
};

type TEntity<T extends keyof SgEntitySelectEntityFetchMap> = SgEntitySelectEntityFetchMap[T]['entity'];
type TQuery<T extends keyof SgEntitySelectEntityFetchMap> = SgEntitySelectEntityFetchMap[T]['query'];

export interface SgEntityFetchAdapter<T extends keyof SgEntitySelectEntityFetchMap> {
  readonly key: T;
  readonly metadata: {
    idField: keyof TEntity<T>;
    labelField: keyof TEntity<T> | Array<keyof TEntity<T>> | string | Array<string>;
    queryField?: { field: keyof TQuery<T>; isArray?: boolean };
  };
  getEntity(id: string | string[]): Observable<TEntity<T> | Array<TEntity<T>>>;
  findEntities(query: TQuery<T>, pageNumber?: number): Observable<SgEntitySelectSearchResponse<T>>;
}

@Injectable({ providedIn: 'root' })
export class SgEntitySelectFetchStore {
  private fetchers = new Map<string, SgEntityFetchAdapter<any>>();

  hasFetcher<T extends keyof SgEntitySelectEntityFetchMap>(key: T): boolean {
    return this.fetchers.has(key);
  }

  getFetcher<T extends keyof SgEntitySelectEntityFetchMap>(key: T): SgEntityFetchAdapter<T> {
    return this.findFetcher(key);
  }

  registerEntityFetcher<T extends keyof SgEntitySelectEntityFetchMap>(adapter: SgEntityFetchAdapter<T>): void {
    this.fetchers.set(adapter.key, adapter);
  }

  private findFetcher<T extends keyof SgEntitySelectEntityFetchMap>(key: T): SgEntityFetchAdapter<T> {
    const fetcher = this.fetchers.get(key) as SgEntityFetchAdapter<T>;
    if (!fetcher) {
      throw new Error(`Could not find entity fetcher with key ${key}`);
    }
    return fetcher;
  }
}
